import { InfoOutlineIcon } from "@chakra-ui/icons"
import {
  Badge,
  BadgeProps,
  HStack,
  Icon,
  Link,
  Skeleton,
  type StackProps,
  Text,
  Tooltip,
} from "@chakra-ui/react"
import { type FC, Fragment } from "react"
import ConditionalRender from "@/common/components/ConditionalRender"
import { getDateFromNow } from "@/common/helpers/date"
import { DRSConfig, DRSInfo, DRSStatus } from "@/governance/types/drs"
import { ArrowUpRight } from "@/ui/components/icons/font-awesome/ArrowUpRight"
import { BadgeDollar } from "@/ui/components/icons/font-awesome/BadgeDollar"
import { CircleXMark } from "@/ui/components/icons/font-awesome/CircleXMark"
import { SealExclamation } from "@/ui/components/icons/font-awesome/SealExclamation"

export const statusConfig = {
  [DRSStatus.ACTIVE]: {
    color: "#0A7D68",
    background: "#CFFCEB",
    icon: BadgeDollar,
    text: "Active",
  },
  [DRSStatus.INACTIVE]: {
    color: "#956F00",
    background: "#FFF1E3",
    icon: SealExclamation,
    text: "Inactive",
  },
  [DRSStatus.GHOST]: {
    color: "#A81B3E",
    background: "#FFF0F0",
    icon: CircleXMark,
    text: "Ghost",
  },
}

export const DRSMarker = ({
  drsInfo,
  config,
  ...props
}: {
  drsInfo: DRSInfo
  config?: DRSConfig | null
} & StackProps) => {
  const shouldShowHighlighting = config?.displayStatusBadge ?? false
  const color = shouldShowHighlighting ? statusConfig[drsInfo.status].color : "gray.600"
  const threshold = config?.defaultThreshold
  
  const tooltipLabel = config?.getDRSStatusTooltip?.(drsInfo, threshold!) ?? 
    (threshold ? `Delegates need a minimum DRS (Delegate Reputation Score) of ${threshold} to be eligible` : "")
  
  return (
    <HStack spacing={1} {...props}>
      <Text color="gray.600" fontSize="sm">
        DRS:
      </Text>
      <Text color={color} fontSize="sm" fontWeight="medium">
        {drsInfo.drsScore}
      </Text>
      <ConditionalRender condition={shouldShowHighlighting}>
        <Tooltip fontSize="md" label={tooltipLabel}>
          <InfoOutlineIcon color="gray.700" h={3} w={3} data-testid="info-icon" />
        </Tooltip>
      </ConditionalRender>
    </HStack>
  )
}

export const DRSProbationBadge = (
  props: BadgeProps & { withTooltip?: boolean } = {},
) => {
  const Wrapper = props.withTooltip ? Tooltip : Fragment
  const tooltipProps = props.withTooltip
    ? {
        label:
          "This delegate is still new and currently on probation. Their voting history is not yet representative.",
      }
    : {}

  return (
    <Wrapper {...tooltipProps}>
      <Badge
        borderRadius="full"
        colorScheme="gray"
        fontSize="sm"
        fontWeight="medium"
        px={2}
        py={1}
        textTransform="none"
        {...props}
      >
        New delegate
      </Badge>
    </Wrapper>
  )
}

export const DRSStatusBadge: FC<
  { status?: DRSStatus; config?: DRSConfig | null } & BadgeProps
> = ({ status, config, ...props }) => {
  if (!status || !config?.displayStatusBadge) return null

  const scheme = statusConfig[status]

  return (
    <Badge
      borderRadius="md"
      color={scheme.color}
      backgroundColor={scheme.background}
      fontSize="sm"
      fontWeight="medium"
      px={2}
      py={1}
      textTransform="none"
      {...props}
    >
      <HStack>
        {scheme.icon && (
          <Icon as={scheme.icon} color={scheme.color} h={4} w={4} />
        )}
        <Text>{scheme.text}</Text>
      </HStack>
    </Badge>
  )
}

type DelegateDRSInfoProps = {
  isLoading: boolean
  isDRSActive?: boolean
  data?: DRSInfo | null
  showNewDelegateBadge?: boolean
  showExternalLink?: boolean
  variant?: "collapsed" | "expanded"
  showTimestamp?: boolean
  config?: DRSConfig | null
}

const DelegateDRSInfo: FC<DelegateDRSInfoProps> = ({
  isLoading,
  isDRSActive,
  data,
  config,
  showNewDelegateBadge = true,
  showExternalLink = true,
  showTimestamp = true,
  variant = "collapsed",
}) => {
  if (isLoading) {
    return (
      <HStack spacing={2} w="full">
        <Skeleton h="30px" w="300px" />
      </HStack>
    )
  }

  if (!data || !isDRSActive || !data.hasInfo) return null
  const threshold = config?.defaultThreshold

  const badges = (
    <>
      <ConditionalRender
        condition={
          showNewDelegateBadge && data.probationSupported && data.isProbation
        }
      >
        <DRSProbationBadge />
      </ConditionalRender>
      <DRSStatusBadge status={data.status} config={config} />
      {threshold && (
        <DRSMarker
          drsInfo={data}
          config={config}
        />
      )}
    </>
  )

  const externalLink = showExternalLink && data.externalLink && (
    <Link
      isExternal
      alignItems="center"
      display="inline-flex"
      href={data.externalLink}
      transition="all 0.2s"
      _hover={{ opacity: 0.7 }}
    >
      <Icon as={ArrowUpRight} boxSize={4} color="gray.600" cursor="pointer" />
    </Link>
  )

  const timestamp = showTimestamp && data.lastUpdated && (
    <Text color="gray.600" fontSize="xs">
      {variant === "expanded" ? "Updated " : ""}
      {getDateFromNow(data.lastUpdated)} ago
    </Text>
  )

  if (variant === "expanded") {
    return (
      <HStack justifyContent="space-between" spacing={4} width="full">
        <HStack spacing={2}>
          {badges}
          {externalLink}
        </HStack>
        {timestamp}
      </HStack>
    )
  }

  return (
    <HStack spacing={2} alignItems="center">
      {badges}
      {timestamp && (
        <HStack spacing={1}>
          <Text color="gray.900" fontSize="lg">
            •
          </Text>
          {timestamp}
        </HStack>
      )}
      {externalLink}
    </HStack>
  )
}

export default DelegateDRSInfo
